import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type {
  CreateUserAttributeRequest,
  UserAttributeDefinition,
  UserAttributeType,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Modal } from './Modal';

interface Props {
  open: boolean;
  onClose: () => void;
}

const TYPE_LABEL: Record<UserAttributeType, string> = {
  text: '텍스트',
  number: '숫자',
  date: '날짜',
  boolean: '예/아니오',
  enum: '선택지',
};

export function UserAttributeManagerModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'user-attributes'],
    queryFn: api.adminUserAttributesList,
    enabled: open,
  });
  const defs = data?.definitions ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<UserAttributeDefinition | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.adminReorderUserAttributes(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-attributes'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : '순서 변경에 실패했습니다.'),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.adminDeleteUserAttribute(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-attributes'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : '삭제에 실패했습니다.'),
  });

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...defs];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    const a = next[idx];
    const b = next[target];
    if (!a || !b) return;
    next[idx] = b;
    next[target] = a;
    reorder.mutate(next.map((d) => d.id));
  };

  if (!open) return null;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="사용자 항목 관리"
        description="직책·부서코드·생년월일 같은 동적 항목을 정의합니다. 정의된 항목은 사용자 추가/편집 폼에 자동 노출됩니다."
        size="lg"
        footer={
          <div className="flex justify-end">
            <Button type="button" onClick={onClose}>
              닫기
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button type="button" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              항목 추가
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-foreground-muted">불러오는 중…</p>
          ) : defs.length === 0 ? (
            <div className="rounded-md border border-border bg-surface-muted/40 px-3 py-6 text-center text-sm text-foreground-muted">
              아직 등록된 항목이 없습니다. 위 "항목 추가" 로 시작하세요.
            </div>
          ) : (
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
                  <tr>
                    <th className="px-3 py-2 w-20">순서</th>
                    <th className="px-3 py-2 text-left">라벨</th>
                    <th className="px-3 py-2 text-left">key</th>
                    <th className="px-3 py-2 text-left">타입</th>
                    <th className="px-3 py-2 text-left">필수</th>
                    <th className="px-3 py-2 text-right">액션</th>
                  </tr>
                </thead>
                <tbody>
                  {defs.map((d, i) => (
                    <tr key={d.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <div className="inline-flex items-center gap-1">
                          <IconBtn aria-label="위로" onClick={() => move(i, -1)} disabled={i === 0}>
                            <ArrowUp className="size-3.5" />
                          </IconBtn>
                          <IconBtn
                            aria-label="아래로"
                            onClick={() => move(i, 1)}
                            disabled={i === defs.length - 1}
                          >
                            <ArrowDown className="size-3.5" />
                          </IconBtn>
                        </div>
                      </td>
                      <td className="px-3 py-2 font-medium">{d.label}</td>
                      <td className="px-3 py-2 font-mono text-xs">{d.key}</td>
                      <td className="px-3 py-2">
                        {TYPE_LABEL[d.type]}
                        {d.type === 'enum' && d.options ? (
                          <span className="ml-2 text-xs text-foreground-muted">
                            ({d.options.length}개)
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">{d.required ? '필수' : '선택'}</td>
                      <td className="px-3 py-2 text-right">
                        <IconBtn aria-label="편집" onClick={() => setEditing(d)}>
                          <Pencil className="size-3.5" />
                        </IconBtn>
                        <IconBtn
                          aria-label="삭제"
                          danger
                          onClick={() => {
                            if (
                              window.confirm(
                                `'${d.label}' 항목을 삭제하시겠습니까? (기존 사용자 값은 보존)`,
                              )
                            ) {
                              del.mutate(d.id);
                            }
                          }}
                        >
                          <Trash2 className="size-3.5" />
                        </IconBtn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      {createOpen ? (
        <AttributeFormModal mode="create" onClose={() => setCreateOpen(false)} />
      ) : null}
      {editing ? (
        <AttributeFormModal mode="edit" def={editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

function IconBtn({
  children,
  onClick,
  disabled,
  danger,
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
} & React.AriaAttributes) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...rest}
      className={cn(
        'size-7 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        danger ? 'hover:text-destructive' : '',
      )}
    >
      {children}
    </button>
  );
}

export function AttributeFormModal(
  props:
    | { mode: 'create'; onClose: () => void }
    | { mode: 'edit'; def: UserAttributeDefinition; onClose: () => void },
) {
  const qc = useQueryClient();
  const isEdit = props.mode === 'edit';
  const def = isEdit ? props.def : null;
  const [key, setKey] = useState(def?.key ?? '');
  const [label, setLabel] = useState(def?.label ?? '');
  const [type, setType] = useState<UserAttributeType>(def?.type ?? 'text');
  const [required, setRequired] = useState(def?.required ?? false);
  const [showInDirectory, setShowInDirectory] = useState(def?.showInDirectory ?? false);
  const [optionsText, setOptionsText] = useState((def?.options ?? []).join('\n'));
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: CreateUserAttributeRequest) => api.adminCreateUserAttribute(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'user-attributes'] });
      props.onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '항목 생성에 실패했습니다.'),
  });

  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: {
        label?: string;
        type?: UserAttributeType;
        options?: string[] | null;
        required?: boolean;
        showInDirectory?: boolean;
      };
    }) => api.adminUpdateUserAttribute(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'user-attributes'] });
      props.onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '항목 수정에 실패했습니다.'),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const options =
      type === 'enum'
        ? optionsText
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean)
        : null;
    if (type === 'enum' && (!options || options.length === 0)) {
      setError('선택지 타입에는 최소 1개의 옵션이 필요합니다.');
      return;
    }
    if (isEdit && def) {
      update.mutate({
        id: def.id,
        body: { label, type, options, required, showInDirectory },
      });
    } else {
      create.mutate({ key, label, type, options, required, showInDirectory });
    }
  };

  return (
    <Modal
      open
      onClose={props.onClose}
      title={isEdit ? '항목 편집' : '항목 추가'}
      size="md"
      layer="high"
    >
      <form onSubmit={submit} className="space-y-3">
        <Field
          label="key (식별자)"
          htmlFor="ua-key"
          hint={
            isEdit
              ? '한 번 등록한 key 는 변경할 수 없습니다.'
              : '영문 소문자/숫자/밑줄 (예: department_code).'
          }
        >
          <Input
            id="ua-key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={isEdit}
            placeholder="department_code"
          />
        </Field>

        <Field label="라벨" htmlFor="ua-label" hint="사용자 폼에 표시될 한글 이름.">
          <Input
            id="ua-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="부서코드"
          />
        </Field>

        <Field label="타입" htmlFor="ua-type">
          <select
            id="ua-type"
            value={type}
            onChange={(e) => setType(e.target.value as UserAttributeType)}
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
          >
            <option value="text">텍스트</option>
            <option value="number">숫자</option>
            <option value="date">날짜</option>
            <option value="boolean">예/아니오</option>
            <option value="enum">선택지</option>
          </select>
        </Field>

        {type === 'enum' ? (
          <Field label="선택지 (줄당 1개)" htmlFor="ua-options">
            <textarea
              id="ua-options"
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              rows={5}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono"
              placeholder="CEO&#10;부서장&#10;팀장&#10;사원"
            />
          </Field>
        ) : null}

        <Field label="필수 입력" htmlFor="ua-required">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              id="ua-required"
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              className="size-4"
            />
            <span>{required ? '필수' : '선택'}</span>
          </label>
        </Field>

        <Field
          label="검색 노출"
          htmlFor="ua-directory"
          hint="켜면 일반 사용자가 공유·그룹 화면에서 사람을 검색할 때 이 속성이 함께 표시됩니다 (동명이인 구분)."
        >
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              id="ua-directory"
              type="checkbox"
              checked={showInDirectory}
              onChange={(e) => setShowInDirectory(e.target.checked)}
              className="size-4"
            />
            <span>{showInDirectory ? '검색 결과에 표시' : '숨김'}</span>
          </label>
        </Field>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            취소
          </Button>
          <Button type="submit" disabled={create.isPending || update.isPending}>
            {isEdit
              ? update.isPending
                ? '저장 중…'
                : '저장'
              : create.isPending
                ? '생성 중…'
                : '추가'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
